import { openWorkspaceDb } from '@/offline/db'
import { decodeText, decryptChunk, deriveKey, fromB64, sha256Hex } from './crypto'
import {
  BACKUP_FORMAT,
  BACKUP_TABLES,
  FORMAT_VERSION,
  type BackupBundle,
  type BackupRecords,
  type BackupTable,
} from './manifest'

export class BackupError extends Error {}

/** Structural validation before trusting any bytes. */
export function parseBundle(raw: string): BackupBundle {
  let obj: unknown
  try {
    obj = JSON.parse(raw)
  } catch {
    throw new BackupError('File is not valid JSON.')
  }
  const b = obj as Partial<BackupBundle>
  if (b?.format !== BACKUP_FORMAT) throw new BackupError('Not a Mobile Trading Pro backup file.')
  if (b.format_version !== FORMAT_VERSION) {
    throw new BackupError(`Unsupported backup version ${String(b.format_version)} (expected ${FORMAT_VERSION}).`)
  }
  if (!Array.isArray(b.chunks) || b.chunks.length === 0) throw new BackupError('Backup has no chunks.')
  if (!b.kdf || typeof b.kdf.iterations !== 'number') throw new BackupError('Backup KDF metadata is missing.')
  return b as BackupBundle
}

/**
 * Decrypt + verify a bundle with only the recovery secret. Every chunk is
 * SHA-256 checked, the reassembled plaintext is SHA-256 checked against the
 * manifest, and per-table counts are reconciled. Throws on any mismatch.
 * Proves the plan §172/§236 gate: restore needs NO database and NO server keys.
 */
export async function decryptBundle(bundle: BackupBundle, secret: string): Promise<BackupRecords> {
  const key = await deriveKey(secret, fromB64(bundle.kdf.salt), bundle.kdf.iterations)

  const ordered = [...bundle.chunks].sort((a, b) => a.index - b.index)
  const parts: Uint8Array[] = []
  let total = 0
  for (const chunk of ordered) {
    let plain: Uint8Array
    try {
      plain = await decryptChunk(key, fromB64(chunk.nonce), fromB64(chunk.ciphertext))
    } catch {
      throw new BackupError(`Chunk ${chunk.index} failed to decrypt — wrong recovery secret or corrupted file.`)
    }
    const hash = await sha256Hex(plain)
    if (hash !== chunk.plaintext_sha256) throw new BackupError(`Chunk ${chunk.index} failed integrity check.`)
    if (plain.length !== chunk.plaintext_bytes) throw new BackupError(`Chunk ${chunk.index} has wrong byte length.`)
    parts.push(plain)
    total += plain.length
  }

  if (total !== bundle.plaintext_bytes) throw new BackupError('Reassembled size does not match the manifest.')

  const merged = new Uint8Array(total)
  let off = 0
  for (const p of parts) {
    merged.set(p, off)
    off += p.length
  }
  const fullHash = await sha256Hex(merged)
  if (fullHash !== bundle.records_sha256) throw new BackupError('Backup integrity check failed (records hash).')

  let records: BackupRecords
  try {
    records = JSON.parse(decodeText(merged)) as BackupRecords
  } catch {
    throw new BackupError('Decrypted payload is not valid JSON.')
  }

  for (const table of BACKUP_TABLES) {
    const rows = records[table]
    if (!Array.isArray(rows)) throw new BackupError(`Missing table "${table}" in backup.`)
    if (rows.length !== bundle.counts[table as BackupTable]) {
      throw new BackupError(`Record count mismatch for "${table}".`)
    }
  }
  return records
}

export interface RestoreResult {
  workspace_id: string
  counts: Record<BackupTable, number>
  watermark: string | null
  created_at: string
}

/**
 * Reconstruct a workspace from a bundle. Defaults to the backed-up workspace id
 * (rebuilding a lost/erased local DB); pass targetWorkspaceId to restore into an
 * isolated workspace, in which case every record is rewritten to that id.
 */
export async function restoreFromBundle(
  bundle: BackupBundle,
  secret: string,
  targetWorkspaceId: string = bundle.workspace_id,
): Promise<RestoreResult> {
  const records = await decryptBundle(bundle, secret)
  const rewrite = targetWorkspaceId !== bundle.workspace_id
  const db = openWorkspaceDb(targetWorkspaceId)

  const counts = {} as Record<BackupTable, number>
  await db.transaction('rw', BACKUP_TABLES.map((t) => db.table(t)), async () => {
    for (const table of BACKUP_TABLES) {
      const rows = (records[table] as Record<string, unknown>[]).map((r) =>
        rewrite ? { ...r, workspace_id: targetWorkspaceId } : r,
      )
      await db.table(table).bulkPut(rows)
      counts[table] = rows.length
    }
  })

  return { workspace_id: targetWorkspaceId, counts, watermark: bundle.watermark, created_at: bundle.created_at }
}

export function describeBundle(bundle: BackupBundle): { total: number; tables: { name: string; count: number }[] } {
  const tables = BACKUP_TABLES.map((name) => ({ name, count: bundle.counts[name] ?? 0 }))
  return { total: tables.reduce((s, t) => s + t.count, 0), tables }
}
